'use client';

import {useCallback, useEffect, useLayoutEffect, useRef, useState, type UIEvent} from 'react';

import {useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {IconButton} from '@udemy-v2/react-core-components';

import {trackTestimonialCtaSelected} from '@/features/communities/lib/tracking';

import {TestimonialCard, type Testimonial} from './testimonial-card.react-component';

import styles from './instructor-testimonials.module.scss';

/** Placeholder the heading copy is split on so the whole sentence stays one msgid. */
const HIGHLIGHT_PLACEHOLDER = '%(highlight)s';

/**
 * The carousel opens on the second card, so the design's centred slide is the one on screen
 * with its neighbours peeking either side. Clamped against the supplied list, since a shorter
 * one may not have this many slides.
 */
const INITIAL_SLIDE_INDEX = 1;

/**
 * Positions the track before the browser paints, so the opening slide is never seen sliding
 * into place. React warns that layout effects do nothing when rendering on the server, so it
 * degrades to `useEffect` there — where the position is irrelevant anyway.
 */
/* istanbul ignore next -- jsdom is always the browser arm; the server one cannot be exercised */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export interface InstructorTestimonialsProps {
    /**
     * Testimonials to page through. Defaults to the three drawn in Figma, assembled
     * inside the component so every string stays a literal `gettext(…)` call.
     */
    testimonials?: Testimonial[];
}

/**
 * Instructor testimonial carousel — Figma node 6553:175312 (desktop, the 2026-09-18 refresh;
 * the heading is 6553:175310) and 5260:160544 (mobile, not refreshed alongside — see the
 * component's copy for what that means for the second card), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A centred heading over a peek carousel: one {@link TestimonialCard} centred in the
 * viewport with its neighbours showing at both edges, and a pager below (previous, dot
 * indicators, next). Paging is CSS scroll snapping — the buttons and dots centre a slide
 * with `scrollIntoView`, so touch swiping, trackpad scrolling and the controls all land on
 * the same positions, and the writing direction is the browser's problem rather than ours.
 *
 * It opens on the *second* slide, as the design draws it, positioned before first paint —
 * see {@link INITIAL_SLIDE_INDEX} and the layout effect below.
 *
 * The carousel loops: with more than one slide, a hidden clone of the last slide is
 * prepended and a clone of the first is appended, so there is always a slide to peek at and
 * to snap to past either end. The pager never disables, and paging or swiping past an end
 * lands on a clone that looks identical to the real slide it stands in for; the track is
 * then silently re-anchored to that real slide with an `instant` scroll — a jump nothing is
 * left visibly watching — by whichever of `resolvePendingReanchor`'s three triggers fires
 * first: the native `scrollend` event, the `SETTLE_DELAY_MS` fallback for engines without
 * one, or the pager being clicked again before either has. The clones are `inert` and
 * `aria-hidden`, so they never reach the accessibility tree or the tab order, and
 * `getAllByRole('listitem')` in tests only ever sees the real slides.
 *
 * The design-system `Carousel` was not used here: it pages flush against the leading edge
 * of its scroll port and renders its pager buttons over the slides, where this design
 * centres the active slide between two partly visible ones and puts the pager below the
 * card with dot indicators.
 */
/**
 * Fallback ceiling on how long the track waits to sit still before a clone is re-anchored to
 * its real slide, for browsers with no `scrollend` event (see the effect that listens for
 * it, below). Long enough that the tail end of a `scroll-behavior: smooth` glide or touch
 * momentum has finished, since `handleScroll` fires continuously while either is still in
 * flight and re-anchoring mid-glide would cut it short instead of waiting for it to land.
 */
const SETTLE_DELAY_MS = 150;

/**
 * The scroll's own `behavior: 'instant'` is supposed to override the track's CSS
 * `scroll-behavior: smooth`, but WebKit (Safari and every iOS browser, since they all run
 * on it) has a history of ignoring that override and animating the "instant" scroll anyway.
 * Every re-anchor firing as a smooth scroll makes it a `scroll` event in its own right,
 * which re-enters `handleScroll` mid-flight and can schedule another re-anchor on top of
 * whatever native touch momentum is still landing — the runaway, self-scrolling carousel
 * this works around. Toggling the inline style off for the duration of the call is a belt
 * a browser that already honours `instant` doesn't need, but costs it nothing either.
 *
 * The relative move is applied as an absolute `scrollTo`, never `scrollBy`: iOS WebKit (17
 * and 26 at least; Apple Developer Forums thread 739408) applies a non-smooth `scrollBy`'s
 * delta *twice*. Each re-anchor then overshoots by one real-to-clone distance, clamps onto
 * the *other* clone, and schedules another re-anchor — an endless flicker between the first
 * and last slides from page load. `scrollLeft + left` is the same target in right-to-left
 * too, since `scrollLeft` counts negative from the start edge there.
 */
function scrollByInstantly(track: HTMLElement, left: number) {
    const previousScrollBehavior = track.style.scrollBehavior;

    track.style.scrollBehavior = 'auto';
    track.scrollTo?.({left: track.scrollLeft + left, behavior: 'instant'});
    track.style.scrollBehavior = previousScrollBehavior;
}

export function InstructorTestimonials({testimonials}: InstructorTestimonialsProps) {
    const {gettext, interpolate} = useI18n();
    const trackRef = useRef<HTMLUListElement>(null);
    const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    // Set while the track is resting on a clone that hasn't been re-anchored to its real
    // slide yet — by `scrollend`, by the `SETTLE_DELAY_MS` fallback, or (see `step`) by
    // being resolved early because the pager was clicked again first.
    const pendingReanchorRef = useRef<{cloneIndex: number; realLoopedIndex: number} | null>(null);

    // One msgid for the whole heading so translators control word order around the
    // highlighted word, rather than receiving two disconnected halves.
    const [beforeHighlight, afterHighlight] = gettext('Instructors love their %(highlight)s').split(
        HIGHLIGHT_PLACEHOLDER,
    );

    const slides =
        testimonials ??
        ([
            {
                slug: 'frank-kane',
                profileUrl: '/user/frankkane',
                // Analytics only, for the CTA's `PromotionSelected` event (Rabia,
                // 2026-09-18). Production ids: they do not correspond to anything in the
                // anonymised dev catalogue, so on a dev deploy the event carries a pair
                // that dev's own community list does not contain.
                instructorId: '13148312',
                communityId: '25',
                quote: gettext(
                    'More human connection is what people crave in our digital society! I’m excited to talk directly to our AI engineering learners, forge real connections, and create a community that faces this fast-moving field together.',
                ),
                name: 'Frank Kane',
                role: gettext('Engineer'),
                ctaLabel: interpolate(gettext('More about %(name)s'), {name: 'Frank'}, true),
                tone: 'green',
                // Figma sizes each quote's box individually, and that is what decides where
                // its lines break — 374/432/542 across the three cards.
                quoteInlineSize: 374,
            },
            {
                slug: 'team-rondi',
                // "Team RonDi" combines Ronny and Diego into one testimonial, and the
                // design names no joint destination, so the CTA points at Ronny's profile
                // — supplied by Rabia, 2026-09-18. The ids below attribute the click to
                // the same instructor, which is what keeps the link and the event
                // consistent with each other; the card still names two people.
                profileUrl: '/user/ronny-hermosa',
                quote: gettext(
                    'Courses are great for learning the skills, but community is where you practice them: take part in challenges, show what you’ve made, ask questions, get feedback, share the wins (and the failures ), and connect with people who are just as excited about Canva as you are.',
                ),
                name: 'Team RonDi',
                role: gettext('Canva Verified Experts'),
                ctaLabel: interpolate(gettext('More about %(name)s'), {name: 'Team RonDi'}, true),
                instructorId: '26625950',
                communityId: '17',
                tone: 'purple',
                quoteInlineSize: 432,
            },
            {
                slug: 'diego-davila',
                profileUrl: '/user/diego-davila',
                // Analytics only, as above.
                instructorId: '801404',
                communityId: '24',
                quote: gettext(
                    'I have seen incredibly talented people struggle simply because the right clients cannot find them. AI made it easy for everyone to publish. It did not make anyone easier to find. Every week we build the next piece together, and I get to watch them go from invisible to irresistible.',
                ),
                name: 'Diego Davila',
                role: gettext('Digital Entrepreneur'),
                ctaLabel: interpolate(gettext('More about %(name)s'), {name: 'Diego'}, true),
                tone: 'orange',
            },
        ] satisfies Testimonial[]);

    const initialIndex = Math.max(0, Math.min(INITIAL_SLIDE_INDEX, slides.length - 1));
    const [activeIndex, setActiveIndex] = useState(initialIndex);

    // More than one slide is what makes looping meaningful — with exactly one, "next" and
    // "previous" would only ever point back at itself. `offset` is how far a real slide's
    // index sits from its position in the DOM once the clones are prepended.
    const hasLoop = slides.length > 1;
    const offset = hasLoop ? 1 : 0;

    /**
     * The rendered track: the real slides, plus — when there is more than one — a clone of
     * the last slide before them and a clone of the first slide after. Clones are `inert` and
     * `aria-hidden`, so they carry no interactive duplicate of the CTA button into the tab
     * order or the accessibility tree; they exist only so there is always something to peek
     * at, and to snap to, past either end.
     */
    const displaySlides = hasLoop
        ? [
              {
                  ...slides[slides.length - 1]!,
                  key: 'clone-start',
                  realIndex: slides.length - 1,
                  isClone: true,
              },
              ...slides.map((testimonial, realIndex) => ({
                  ...testimonial,
                  key: testimonial.slug,
                  realIndex,
                  isClone: false,
              })),
              {...slides[0]!, key: 'clone-end', realIndex: 0, isClone: true},
          ]
        : slides.map((testimonial, realIndex) => ({
              ...testimonial,
              key: testimonial.slug,
              realIndex,
              isClone: false,
          }));

    /**
     * Centres the opening slide inside the track, and only inside the track. `scrollIntoView`
     * is deliberately not used here the way the controls below use it: on load this section
     * sits well below the fold, and the block axis would drag the whole document down to the
     * carousel. Measuring the offset from rendered boxes and applying it as a relative move keeps
     * it correct in right-to-left too. {@link scrollByInstantly}
     * forces the track past its `scroll-behavior: smooth`, so the carousel simply starts there.
     */
    useIsomorphicLayoutEffect(() => {
        // The `<ul>` renders unconditionally, so the ref is attached by the time a layout
        // effect runs; the slide is what may be missing, when the list is empty.
        const track = trackRef.current!;
        const slide = track.children[initialIndex + offset];

        if (!slide) {
            return;
        }

        const trackBox = track.getBoundingClientRect();
        const slideBox = slide.getBoundingClientRect();

        scrollByInstantly(
            track,
            slideBox.left + slideBox.width / 2 - (trackBox.left + trackBox.width / 2),
        );
    }, [initialIndex, offset]);

    /**
     * Performs a pending clone re-anchor immediately, if there is one, instead of waiting for
     * `scrollend` or the {@link SETTLE_DELAY_MS} fallback. `step` calls this before computing
     * its own move, which is what keeps a second pager click correct if it lands before the
     * *first* click's re-anchor has happened yet — see `step`'s own comment.
     */
    const resolvePendingReanchor = useCallback(() => {
        const track = trackRef.current;
        const pending = pendingReanchorRef.current;

        clearTimeout(settleTimeoutRef.current);

        if (!track || !pending) {
            return;
        }

        pendingReanchorRef.current = null;

        const cloneBox = track.children[pending.cloneIndex]?.getBoundingClientRect();
        const realBox = track.children[pending.realLoopedIndex]?.getBoundingClientRect();

        if (cloneBox && realBox) {
            scrollByInstantly(track, realBox.left - cloneBox.left);
        }
    }, []);

    // `scrollend` fires once, exactly when the browser considers scrolling — smooth or
    // momentum — finished, which is a far tighter bound than the fixed-delay fallback above
    // can be: waiting long enough to be *sure* an unknown-length glide has landed necessarily
    // means sometimes waiting after it already has, and every extra millisecond is one the
    // clone's missing outer neighbour (there is nothing past it to peek at) is visible for.
    // Older engines without it fall back to `SETTLE_DELAY_MS` alone; harmless to listen for
    // both, since `resolvePendingReanchor` is a no-op once either has already run.
    useEffect(() => {
        const track = trackRef.current;

        if (!track) {
            return undefined;
        }

        track.addEventListener('scrollend', resolvePendingReanchor);

        return () => {
            track.removeEventListener('scrollend', resolvePendingReanchor);
            clearTimeout(settleTimeoutRef.current);
        };
    }, [resolvePendingReanchor]);

    /**
     * Centres a slide and records it as active straight away, so the dots respond to the
     * click rather than waiting on the scroll that follows. The index wraps rather than
     * clamps — paging past either end is exactly how the loop is reached from the controls —
     * and the scroll target is offset by the leading clone, when there is one.
     *
     * This is a direct jump, so it always targets the real slide: correct for the dots, which
     * pick an arbitrary index, but wrong for the pager buttons — see {@link step}.
     */
    const goToSlide = useCallback(
        (index: number) => {
            resolvePendingReanchor();

            // `% 0` is `NaN`, and `children[NaN]` is `undefined` — the optional chains below
            // already make that a no-op, the same way an empty list did before this wrapped.
            const wrapped = ((index % slides.length) + slides.length) % slides.length;

            setActiveIndex(wrapped);
            trackRef.current?.children[wrapped + offset]?.scrollIntoView?.({
                // `nearest` on the block axis so centring a slide never scrolls the page.
                block: 'nearest',
                inline: 'center',
            });
        },
        [slides.length, offset, resolvePendingReanchor],
    );

    /**
     * Moves the pager exactly one position — the opposite of `goToSlide`'s direct jump.
     * Wrapping past either end has to land on the *adjacent* clone, not jump clear across the
     * track to the real slide it stands in for: `scrollIntoView` takes the shortest path, so
     * targeting the real slide directly would visibly reverse direction (e.g. a second
     * "previous" click, from the first real slide, would scroll all the way right to reach
     * the real last slide, rather than continuing left onto its clone). The dot still updates
     * to the wrapped *real* index immediately; `handleScroll` re-anchors the clone to that
     * real slide once the scroll settles, same as a swipe past the end does.
     *
     * Resolving a pending re-anchor *first* is what makes a fast second click keep going the
     * same direction rather than reversing: without it, `activeIndex` already names the real
     * slide the previous click wrapped to (it is set eagerly, not on landing), but the track
     * itself is still physically resting on that slide's clone, one position short — offsetting
     * from `activeIndex` would then land one step off from wherever the track actually is.
     * Resolving first is a no-op instant scroll when there is nothing pending, and otherwise
     * brings the two back in sync before the next offset is applied.
     */
    const step = useCallback(
        (direction: 1 | -1) => {
            resolvePendingReanchor();

            const wrapped =
                (((activeIndex + direction) % slides.length) + slides.length) % slides.length;

            setActiveIndex(wrapped);
            trackRef.current?.children[activeIndex + offset + direction]?.scrollIntoView?.({
                block: 'nearest',
                inline: 'center',
            });
        },
        [activeIndex, slides.length, offset, resolvePendingReanchor],
    );

    /**
     * Swiping and trackpad scrolling bypass the controls, so the active slide is read back
     * from the scroll position: whichever slide's centre is closest to the track's.
     * Comparing rendered boxes keeps this correct in right-to-left, where `scrollLeft`
     * would not be.
     *
     * Landing on a clone is the loop closing: it looks identical to the real slide it stands
     * in for, so the track is silently re-anchored to that real slide with an `instant`
     * scroll — a jump nothing is left visibly watching, since the two positions render the
     * same card — which leaves a real slide (and its own neighbouring clone) to keep scrolling
     * into on the next swipe in the same direction.
     *
     * The active dot updates on every scroll event, live, same as before — only the
     * re-anchor itself is deferred, to `resolvePendingReanchor` above. Doing it immediately
     * would fire on every intermediate position a `scroll-behavior: smooth` glide (or touch
     * momentum) passes through on its way to the clone, snapping the track over before the
     * glide arrives rather than after — which reads as the animation being cut short rather
     * than as no animation happening at all.
     */
    const handleScroll = useCallback(
        (event: UIEvent<HTMLUListElement>) => {
            const track = event.currentTarget;
            const trackRect = track.getBoundingClientRect();
            const trackCentre = trackRect.left + trackRect.width / 2;
            let nearest = 0;
            let nearestDistance = Infinity;

            Array.from(track.children).forEach((slide, index) => {
                const rect = slide.getBoundingClientRect();
                const distance = Math.abs(rect.left + rect.width / 2 - trackCentre);

                if (distance < nearestDistance) {
                    nearestDistance = distance;
                    nearest = index;
                }
            });

            const lastChildIndex = track.children.length - 1;
            const isStartClone = nearest === 0;
            const landedOnClone = hasLoop && (isStartClone || nearest === lastChildIndex);

            setActiveIndex(
                landedOnClone ? (isStartClone ? slides.length - 1 : 0) : nearest - offset,
            );

            clearTimeout(settleTimeoutRef.current);

            if (!landedOnClone) {
                pendingReanchorRef.current = null;
                return;
            }

            // Where the real slide this clone stands in for actually lives in the track.
            pendingReanchorRef.current = {
                cloneIndex: nearest,
                realLoopedIndex: isStartClone ? slides.length : 1,
            };
            settleTimeoutRef.current = setTimeout(resolvePendingReanchor, SETTLE_DELAY_MS);
        },
        [hasLoop, offset, slides.length, resolvePendingReanchor],
    );

    return (
        <section className={styles['testimonials']} data-purpose="instructor-testimonials">
            <div className={styles['inner']}>
                <h2 className={styles['heading']}>
                    {beforeHighlight}
                    <span className={styles['heading-highlight']}>{gettext('communities')}</span>
                    {afterHighlight}
                </h2>
            </div>

            {/* Full-bleed: the neighbouring slides have to reach the viewport edges, and
                the slide width is measured against this box (`100cqi` in the module). */}
            <div
                className={styles['carousel']}
                role="group"
                aria-roledescription={gettext('carousel')}
                aria-label={gettext('Instructor testimonials')}
            >
                <ul
                    ref={trackRef}
                    className={styles['track']}
                    onScroll={handleScroll}
                    // Scrollable regions need to be reachable by keyboard; the pager
                    // buttons below cover paging itself.
                    tabIndex={0}
                    aria-label={gettext('Testimonials')}
                >
                    {displaySlides.map(({key, isClone, realIndex, ...testimonial}) => (
                        <li
                            key={key}
                            className={styles['slide']}
                            // Clones are a scroll-snap target only — hidden from assistive
                            // tech and, with `inert`, from focus and the tab order too, so
                            // the CTA button inside them is never a reachable duplicate.
                            aria-hidden={isClone || undefined}
                            inert={isClone || undefined}
                            aria-roledescription={isClone ? undefined : gettext('slide')}
                            aria-label={
                                isClone
                                    ? undefined
                                    : interpolate(
                                          gettext('%(index)s of %(total)s'),
                                          {index: realIndex + 1, total: slides.length},
                                          true,
                                      )
                            }
                        >
                            <TestimonialCard
                                {...testimonial}
                                isInitiallyVisible={!isClone && realIndex === initialIndex}
                                // Not wired on a clone. `inert` already puts its CTA out of
                                // reach, so this is belt and braces against a click being
                                // attributed twice by an engine that does not honour it.
                                onSelected={
                                    isClone
                                        ? undefined
                                        : () => trackTestimonialCtaSelected(testimonial)
                                }
                            />
                        </li>
                    ))}
                </ul>

                <div className={styles['pager']}>
                    <IconButton
                        round
                        size="small"
                        udStyle="knockout-solid"
                        className={styles['pager-button']}
                        disabled={!hasLoop}
                        onClick={() => step(-1)}
                        aria-label={gettext('Previous testimonial')}
                    >
                        <Icon glyph="previous" label={false} />
                    </IconButton>

                    <div className={styles['dots']}>
                        {slides.map((testimonial, index) => (
                            <button
                                key={testimonial.slug}
                                type="button"
                                className={styles['dot']}
                                aria-current={index === activeIndex}
                                onClick={() => goToSlide(index)}
                                aria-label={interpolate(
                                    gettext('Show the testimonial from %(name)s'),
                                    {name: testimonial.name},
                                    true,
                                )}
                            />
                        ))}
                    </div>

                    <IconButton
                        round
                        size="small"
                        udStyle="knockout-solid"
                        className={styles['pager-button']}
                        disabled={!hasLoop}
                        onClick={() => step(1)}
                        aria-label={gettext('Next testimonial')}
                    >
                        <Icon glyph="next" label={false} />
                    </IconButton>
                </div>
            </div>
        </section>
    );
}
